import React from 'react';

// GOV13 Step 6 — presentation-only usage card. Renders STRICTLY from one metric of the server usage projection
// (/billing/me/usage). It NEVER infers usage, NEVER renders a missing value as 0, and NEVER renders a missing
// allowance as Unlimited. source_status drives the state: LIVE shows values (overage shown truthfully), CALCULATING
// shows "Calculating usage…" (+ reason), UNAVAILABLE shows "Usage unavailable" (+ safe reason). Unlimited is shown
// only when the canonical allowance is Unlimited.
const fmtAllow = (m) => (m.unlimited ? 'Unlimited' : (m.effective_allowance ?? m.allowance ?? '—'));

export default function UsageMetricCard({ metric }) {
  if (!metric) return null;
  const t = `usage-${metric.metric_key}`;
  const status = metric.source_status;
  // Overage is truthful from either the explicit `overage` field (period metrics: base_remaining is clamped >=0)
  // or a negative `remaining` (byte-based storage). Purchased pack capacity is shown in Credits & Usage.
  const overAmt = (typeof metric.overage === 'number' && metric.overage > 0) ? metric.overage
    : (typeof metric.remaining === 'number' && metric.remaining < 0 ? Math.abs(metric.remaining) : 0);
  const over = status === 'LIVE' && !metric.unlimited && overAmt > 0;

  return (
    <div className="border rounded-lg p-3 bg-white" data-testid={`${t}-card`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-900">{metric.display_name}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
          status === 'LIVE' ? 'bg-green-100 text-green-700'
            : status === 'CALCULATING' ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-600'}`}
          data-testid={`${t}-status`}>{status}</span>
      </div>

      {status === 'LIVE' ? (
        <div className="mt-1">
          <div className="text-lg font-semibold" data-testid={`${t}-value`}>
            <span data-testid={`${t}-used`}>{metric.used}</span>
            <span className="text-sm text-gray-400"> / <span data-testid={`${t}-allowance`}>{fmtAllow(metric)}</span> {metric.unit}</span>
          </div>
          {!metric.unlimited && typeof metric.remaining === 'number' && (
            over
              ? <div className="text-xs text-red-600" data-testid={`${t}-overage`}>Over limit by {overAmt} {metric.unit}</div>
              : <div className="text-xs text-gray-500" data-testid={`${t}-remaining`}>{metric.remaining} {metric.unit} remaining</div>
          )}
          {metric.unlimited && <div className="text-xs text-gray-500" data-testid={`${t}-unlimited`}>Unlimited</div>}
          {metric.purchased_capacity > 0 && (
            <div className="text-[11px] text-blue-600" data-testid={`${t}-purchased`}>+{metric.purchased_capacity} from purchased packs</div>
          )}
        </div>
      ) : status === 'CALCULATING' ? (
        <div className="mt-1 text-sm text-amber-700" data-testid={`${t}-calculating`}>
          Calculating usage…
          {metric.source_reason && <span className="block text-[11px] text-gray-400">{metric.source_reason}</span>}
        </div>
      ) : (
        <div className="mt-1 text-sm text-gray-500" data-testid={`${t}-unavailable`}>
          Usage unavailable
          {metric.source_reason && <span className="block text-[11px] text-gray-400">{metric.source_reason}</span>}
        </div>
      )}

      {metric.period_start && (
        <div className="text-[10px] text-gray-400 mt-1" data-testid={`${t}-period`}>
          Period {String(metric.period_start).slice(0, 10)} – {String(metric.period_end).slice(0, 10)}
        </div>
      )}
      {metric.last_calculated_at && (
        <div className="text-[10px] text-gray-300" data-testid={`${t}-calculated`}>Calculated {String(metric.last_calculated_at).slice(0, 16).replace('T', ' ')}</div>
      )}
    </div>
  );
}
